/**
 * Sales browse view - one row per LS sale (retail included; LS is the
 * core record). Issues chip opens the exception drawer with ALL of the
 * sale's issues; Xero-linked rows deep-link and offer the existing
 * Compare dialog.
 */
import React from 'react'
import { Box, Chip } from '@mui/material'
import {
  renderSaleLsLink,
  renderSaleXeroLink,
} from '../exceptions/sales/saleLinkCells'
import { statusPillChipProps } from '../../components/shared/AppDrawer/chipPresets'
import { formatBusinessDate, formatCurrency } from '../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../exceptions/shared/issuesColumn'
import {
  buildSalesFilterParams,
  sharedSalesFilters,
  SHARED_SALES_FILTER_KEYS,
} from '../exceptions/sales/sharedSalesFilters'
import {
  composeRowSubtitle,
  datePart,
  moneyPart,
  textPart,
} from '../exceptions/shared/rowSubtitle'
import type {
  ColumnGroupDef,
  ExceptionColumnDef,
  ExceptionFilterDef,
} from '../exceptions/types'
import type { DataExplorerViewConfig, DataViewFacets } from './types'
import {
  buildHasIssuesParam,
  hasIssuesFilter,
  last30DaysRange,
  withDetectedAge,
} from './shared'
import inspectorApi from '../../services/inspector/api'

const OUTLET_FILTER_KEY = 'outlet'
const HAS_XERO_LINK_KEY = 'has_xero_link'

const renderLsStatus = (value: any): React.ReactNode =>
  value
    ? React.createElement(
        Chip,
        statusPillChipProps(value, value === 'VOIDED' ? 'warning' : 'neutral'),
      )
    : '-'

// Sale # / Xero cells reuse the canonical sale-link renderers shared with the
// sales exception views. Sale # is centred to match the column header.
const renderLightspeedSale = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => (
  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
    {renderSaleLsLink(row)}
  </Box>
)

const renderXeroLink = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => renderSaleXeroLink(row)

// Group bands (second header row). Frozen identity columns form their own
// band; the rest split into the sale's context and its transaction state.
const salesGroups: ColumnGroupDef[] = [
  { id: 'sale', label: 'Sale', separatorAfter: true },
  { id: 'details', label: 'Details', separatorAfter: true },
  { id: 'transaction', label: 'Transaction' },
]

const columns: ExceptionColumnDef[] = [
  withDetectedAge({ ...buildIssuesColumn(), freeze: 'left', group: 'sale' }),
  {
    key: 'invoice_number',
    label: 'Sale #',
    freeze: 'left',
    group: 'sale',
    align: 'center',
    width: 180,
    minWidth: 180,
    render: renderLightspeedSale,
  },
  {
    key: 'sale_date',
    label: 'Date',
    align: 'left',
    group: 'details',
    render: (value) => formatBusinessDate(value),
    exportFormat: 'date',
  },
  { key: 'customer_name', label: 'Customer', group: 'details', align: 'left' },
  { key: 'outlet_name', label: 'Outlet', group: 'details', align: 'left' },
  {
    key: 'ls_status',
    label: 'LS Status',
    group: 'transaction',
    render: (value) => renderLsStatus(value),
  },
  {
    key: 'total_amount',
    label: 'Total\n(inc tax)',
    align: 'right',
    type: 'currency',
    group: 'transaction',
    render: (value) => formatCurrency(value),
    exportFormat: 'currency',
  },
  {
    key: 'xero_invoice_link',
    label: 'Xero',
    align: 'center',
    group: 'transaction',
    render: renderXeroLink,
  },
]

// Shared sales defs, reshaped for browsing: the date range defaults to
// the last 30 days (clearable to all-time), LS status keeps the shared
// bucket vocabulary, and the credit-note toggle is replaced by browse
// lenses (outlet / Xero-linked / has-issues).
const sharedDateRange = sharedSalesFilters.find(
  (f) => f.key === SHARED_SALES_FILTER_KEYS.saleDateRange,
)
const dateRangeFilter: ExceptionFilterDef =
  sharedDateRange && sharedDateRange.type === 'daterange'
    ? { ...sharedDateRange, defaultValue: last30DaysRange() }
    : {
        key: SHARED_SALES_FILTER_KEYS.saleDateRange,
        type: 'daterange',
        label: 'Sale date',
        defaultValue: last30DaysRange(),
      }
const lsStatusFilter = sharedSalesFilters.find(
  (f) => f.key === SHARED_SALES_FILTER_KEYS.lsStatus,
) as ExceptionFilterDef

const baseFilters: ExceptionFilterDef[] = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Receipt / invoice number or customer',
  },
  dateRangeFilter,
  {
    key: OUTLET_FILTER_KEY,
    type: 'select',
    label: 'Outlet',
    defaultValue: 'all',
    options: [{ value: 'all', label: 'All outlets' }],
  },
  lsStatusFilter,
  {
    key: HAS_XERO_LINK_KEY,
    type: 'select',
    label: 'Xero invoice',
    defaultValue: 'all',
    options: [
      { value: 'all', label: 'Any' },
      { value: 'linked', label: 'Linked' },
      { value: 'unlinked', label: 'Not linked' },
    ],
  },
  hasIssuesFilter,
]

const withOutletOptions = (
  facets: DataViewFacets | null,
): ExceptionFilterDef[] =>
  baseFilters.map((f) =>
    f.key === OUTLET_FILTER_KEY && f.type === 'select'
      ? {
          ...f,
          options: [
            { value: 'all', label: 'All outlets' },
            ...(facets?.outlets ?? []).map((o) => ({
              value: o.id,
              label: o.name,
            })),
          ],
        }
      : f,
  )

export const salesViewConfig: DataExplorerViewConfig = {
  viewKey: 'sales',
  codes: [], // browse view - registry-free
  fpUserAccessId: 'sales',
  pageHeader: {
    title: 'Sales',
    description: 'Browse all Lightspeed sales for this franchise.',
    breadcrumb: 'Sales',
  },
  complexity: 'simple',
  rowKeyField: 'object_id',
  defaultSortColumn: 'sale_date',
  defaultSortDirection: 'desc',
  serverTable: { defaultPageSize: 50, pageSizeOptions: [50, 100], enableSearch: true },
  serverSort: {
    sortableKeys: ['sale_date', 'total_amount', 'errors'],
    defaultSortBy: 'sale_date',
    defaultSortDir: 'desc',
  },
  columns,
  columnGroups: salesGroups,
  disableBulkSelect: true,
  rowDetail: { type: 'compare-sales', headerLabel: 'Actions' },
  filters: baseFilters,
  resolveFilters: withOutletOptions,
  buildFetchParams: (urlState) => ({
    ...buildSalesFilterParams(urlState.filterValues),
    ...buildHasIssuesParam(urlState.filterValues),
    ...(urlState.filterValues?.[OUTLET_FILTER_KEY] &&
    urlState.filterValues[OUTLET_FILTER_KEY] !== 'all'
      ? { outlet: urlState.filterValues[OUTLET_FILTER_KEY] }
      : {}),
    ...(urlState.filterValues?.[HAS_XERO_LINK_KEY] &&
    urlState.filterValues[HAS_XERO_LINK_KEY] !== 'all'
      ? { has_xero_link: urlState.filterValues[HAS_XERO_LINK_KEY] }
      : {}),
  }),
  getRowTitle: (row) => `Sale ${row.invoice_number ?? row.object_id}`,
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.customer_name) || 'Walk-in',
      datePart(row.sale_date),
      moneyPart(row.total_amount),
    ]),
  fetchPage: (fp, params) => inspectorApi.getDataExplorerSales(fp, params),
}
